import { ref, onMounted, onUnmounted, readonly, nextTick } from 'vue'
import { io, type Socket } from 'socket.io-client'
import { useRuntimeConfig } from '#imports'
import { useNotificationStore } from '~/store/notification'
import { useUserStore } from '~/store/user'
import { socketReconnectDelayMs } from '~/utils/socket-reconnect'
import type { NotificationEntity } from '~/generated-api-client'
import type { MwBffSocketTokenResponse } from '~/types/mw-bff'

const socket = ref<Socket | null>(null)
const isConnected = ref(false)
const isConnecting = ref(false)
const listeners = ref<Map<string, Set<Function>>>(new Map())
const componentCount = ref(0)

type SocketConnectError = Error & { data?: { code?: string } }
type EventListener = (...args: any[]) => void

const TOKEN_EXPIRED_CODE = 'token_expired'
const SOCKET_TOKEN_SAFETY_WINDOW_MS = 30_000

let defaultListenersInitialized = false
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
// Retries since the last connection that held; it sets the wait (`utils/socket-reconnect.ts`).
let reconnectAttempt = 0
let connectPromise: Promise<void> | null = null
let socketTokenCache: MwBffSocketTokenResponse | null = null

// VM realtime-progress subscriptions (server only polls the hypervisor for watched
// VMs). Ref-counted so multiple composables on the same page share one watch;
// re-emitted on every (re)connect since server-side state dies with the socket.
const watchedVms = new Map<number, number>()

function sendWatchVm(vmId: number) {
  if (socket.value?.connected) {
    socket.value.emit('watchVm', { vm_id: vmId })
  }
}

function watchVm(vmId: number) {
  if (!vmId) return
  const count = watchedVms.get(vmId) ?? 0
  watchedVms.set(vmId, count + 1)
  if (count === 0) {
    sendWatchVm(vmId)
  }
}

function unwatchVm(vmId: number) {
  if (!vmId) return
  const count = watchedVms.get(vmId) ?? 0
  if (count <= 1) {
    watchedVms.delete(vmId)
    if (socket.value?.connected) {
      socket.value.emit('unwatchVm', { vm_id: vmId })
    }
  } else {
    watchedVms.set(vmId, count - 1)
  }
}

function resubscribeWatchedVms() {
  for (const vmId of watchedVms.keys()) {
    sendWatchVm(vmId)
  }
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

/**
 * Try again later, on the backoff. One retry waits at a time: asking for
 * another while it waits changes nothing.
 */
function scheduleReconnect(userStore: ReturnType<typeof useUserStore>) {
  if (reconnectTimer) {
    return
  }

  const delay = socketReconnectDelayMs(reconnectAttempt)
  reconnectAttempt += 1
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    void reconnect(userStore)
  }, delay)
}

/**
 * One attempt to bring the socket back. Never rejects: any failure but a 401
 * books the next attempt. A 401 has already signed the console out
 * (`fetchAccessToken`), and asking again would only be refused again.
 */
async function reconnect(userStore: ReturnType<typeof useUserStore>) {
  const current = socket.value
  if (!current || current.connected) {
    return
  }
  try {
    await authenticateAndConnect(current as Socket, userStore)
  } catch (error) {
    if (isUnauthorized(error)) {
      return
    }
    console.error('WebSocket reconnection failed', error)
    scheduleReconnect(userStore)
  }
}

function isUnauthorized(error: any) {
  const status = error?.status ?? error?.response?.status ?? error?.data?.statusCode
  return status === 401
}

function hasValidSocketToken() {
  return !!socketTokenCache && socketTokenCache.accessTokenExpiry * 1000 > Date.now() + SOCKET_TOKEN_SAFETY_WINDOW_MS
}

function clearSocketTokenCache() {
  socketTokenCache = null
}

async function fetchAccessToken(userStore: ReturnType<typeof useUserStore>) {
  try {
    if (hasValidSocketToken()) {
      return socketTokenCache!.accessToken
    }

    const { bffFetch } = useMwBff()
    const response = await bffFetch<MwBffSocketTokenResponse>('/socket-token', {
      method: 'GET',
    })
    socketTokenCache = response
    return response.accessToken
  } catch (error: any) {
    if (isUnauthorized(error)) {
      clearSocketTokenCache()
      userStore.setSessionStatus('unauthenticated')
      if (import.meta.client && !isAuthOptionalPath(window.location.pathname)) {
        navigateTo('/login')
      }
    }
    throw error
  }
}

async function authenticateAndConnect(currentSocket: Socket, userStore: ReturnType<typeof useUserStore>) {
  const token = await fetchAccessToken(userStore)
  currentSocket.auth = {
    ...(currentSocket.auth || {}),
    token,
  }
  currentSocket.connect()
}

async function initializeConnection() {
  if (socket.value || connectPromise) {
    return socket.value
  }

  const config = useRuntimeConfig()
  const userStore = useUserStore()

  /*
   * There is no socket for somebody who is not signed in.
   *
   * The socket needs a token from `/socket-token`, which is authenticated, so
   * connecting without a session buys a guaranteed 401 — and the handler for
   * that 401 navigates to `/login`. On the PUBLIC checkout that turned merely
   * rendering the page into an immediate redirect away from it.
   *
   * `unknown` is allowed through: it is the ordinary state on a normal console
   * page before the guard has resolved the session, and refusing it would break
   * every authenticated page. What is refused is the case we positively know
   * cannot work — an auth-optional route with nobody signed in.
   */
  if (
    !userStore.user &&
    import.meta.client &&
    isAuthOptionalPath(window.location.pathname)
  ) {
    return null
  }

  isConnecting.value = true

  connectPromise = (async () => {
    if (!socket.value) {
      socket.value = io(config.public.apiUrl, {
        autoConnect: false,
        transports: ['websocket'],
        withCredentials: false,
        reconnection: false,
      })

      socket.value.on('connect', () => {
        console.log('WebSocket connected')
        isConnected.value = true
        reconnectAttempt = 0
        clearReconnectTimer()
        resubscribeWatchedVms()
      })

      socket.value.on('disconnect', (reason) => {
        console.log('WebSocket disconnected', reason)
        isConnected.value = false

        if (reason === 'io client disconnect' && componentCount.value === 0) {
          return
        }

        scheduleReconnect(userStore)
      })

      socket.value.on('connect_error', (error: SocketConnectError) => {
        console.error('WebSocket connection error:', error)
        isConnected.value = false

        const errorCode = (error?.data?.code ?? error?.message ?? '').toString().toLowerCase()
        if (errorCode === TOKEN_EXPIRED_CODE) {
          // The next attempt asks for a fresh token — through the backoff like
          // any other retry, or a server that keeps calling fresh tokens
          // expired (a skewed clock) would be asked in a tight loop.
          clearSocketTokenCache()
          scheduleReconnect(userStore)
          return
        }

        if (isUnauthorized(error)) {
          clearSocketTokenCache()
          userStore.setSessionStatus('unauthenticated')
          if (import.meta.client && !isAuthOptionalPath(window.location.pathname)) {
            navigateTo('/login')
          }
          return
        }

        scheduleReconnect(userStore)
      })

      if (!defaultListenersInitialized) {
        initializeDefaultListeners()
        defaultListenersInitialized = true
      }
    }

    try {
      if (!socket.value) {
        return
      }
      const activeSocket = socket.value as Socket
      await authenticateAndConnect(activeSocket, userStore)
    } catch (error) {
      /*
       * The first connection could not even ask: `/socket-token` answered an
       * error, or nothing. That used to escape `onMounted` as an unhandled
       * error on every page with a socket, and nothing retried it — the page
       * lived on the polling fallback until reloaded. A 401 has already signed
       * the console out; anything else is retried like a dropped connection.
       */
      if (!isUnauthorized(error)) {
        console.error('WebSocket connection failed', error)
        scheduleReconnect(userStore)
      }
    } finally {
      isConnecting.value = false
      connectPromise = null
    }
  })()

  await connectPromise
  return socket.value
}

function initializeDefaultListeners() {
  if (!socket.value) return

  const notificationStore = useNotificationStore()

  socket.value.on('notification', (data: NotificationEntity) => {
    console.log('notification:', data)
    notificationStore.push(data)
  })

  socket.value.on('vpcCreated', (data) => {
    console.log('VPC created:', data)
    triggerListeners('vpcCreated', data)
  })

  socket.value.on('vpcUpdated', (data) => {
    console.log('VPC updated:', data)
    triggerListeners('vpcUpdated', data)
  })

  socket.value.on('vpcDeleted', (data) => {
    console.log('VPC deleted:', data)
    triggerListeners('vpcDeleted', data)
  })

  socket.value.on('vpnGatewayCreated', (data) => {
    console.log('VPN Gateway created:', data)
    triggerListeners('vpnGatewayCreated', data)
  })

  socket.value.on('vpnGatewayUpdated', (data) => {
    console.log('VPN Gateway updated:', data)
    triggerListeners('vpnGatewayUpdated', data)
  })

  socket.value.on('vpnGatewayDeleted', (data) => {
    console.log('VPN Gateway deleted:', data)
    triggerListeners('vpnGatewayDeleted', data)
  })

  /**
   * A site-to-site tunnel changed state.
   *
   * The API has emitted this since site-to-site shipped — it is what the
   * appliance's own status push turns into — and nothing here listened for it,
   * so it could never reach a page however carefully that page registered. The
   * two screens it exists for fell back to a ten-second poll and to no refresh
   * at all, which is exactly the "somebody at the other end pastes a
   * configuration and this screen does not move" that the feature is watched
   * for.
   */
  socket.value.on('vpnSiteConnectionChanged', (data) => {
    console.log('VPN site connection changed:', data)
    triggerListeners('vpnSiteConnectionChanged', data)
  })

  /**
   * A VPN gateway apply moved to another phase.
   *
   * It carries the whole operation — the phase, the attempt, why a retry is
   * waiting — so the gateway pages patch it in place rather than re-reading
   * the list. Bridged here because nothing reaches a page's listener unless it
   * is: `vpnSiteConnectionChanged` above was emitted for months before anyone
   * noticed no page could ever hear it.
   */
  socket.value.on('vpnGatewayApplyProgress', (data) => {
    triggerListeners('vpnGatewayApplyProgress', data)
  })

  socket.value.on('vmStatusChanged', (data) => {
    console.log('VM status changed:', data)
    triggerListeners('vmStatusChanged', data)
  })

  socket.value.on('jobStatus', (data) => {
    console.log('Job status changed:', data)
    triggerListeners('jobStatus', data)
  })

  socket.value.on('vmOperationProgress', (data) => {
    triggerListeners('vmOperationProgress', data)
  })

  socket.value.on('billingInvoicePaid', (data) => {
    triggerListeners('billingInvoicePaid', data)
  })

  /**
   * Something the home screen's fleet grid shows was measured AFTER its
   * response had gone — today, guest disk. The overview never waits on a guest
   * agent any more: it answers with what is remembered and measures the rest
   * behind the response, then says so here. Carries which instances, never the
   * readings; the page re-reads its overview.
   */
  socket.value.on('fleetVitalsUpdated', (data) => {
    triggerListeners('fleetVitalsUpdated', data)
  })
}

function triggerListeners(eventName: string, ...args: any[]) {
  const eventListeners = listeners.value.get(eventName)
  if (eventListeners) {
    eventListeners.forEach((listener) => {
      try {
        listener(...args)
      } catch (error) {
        console.error(`Error in WebSocket listener for ${eventName}:`, error)
      }
    })
  }
}

function addListener(eventName: string, listener: EventListener) {
  if (!listeners.value.has(eventName)) {
    listeners.value.set(eventName, new Set())
  }
  listeners.value.get(eventName)!.add(listener)
}

function removeListener(eventName: string, listener: EventListener) {
  const eventListeners = listeners.value.get(eventName)
  if (eventListeners) {
    eventListeners.delete(listener)
    if (eventListeners.size === 0) {
      listeners.value.delete(eventName)
    }
  }
}

function disconnectIfUnused() {
  if (componentCount.value === 0 && socket.value) {
    console.log('Disconnecting unused WebSocket connection')
    socket.value.disconnect()
    socket.value = null
    isConnected.value = false
    listeners.value.clear()
    watchedVms.clear()
    defaultListenersInitialized = false
    clearSocketTokenCache()
    clearReconnectTimer()
    reconnectAttempt = 0
  }
}

/**
 * Non-lifecycle access to the shared socket state (safe outside components,
 * e.g. Pinia stores). Does not connect or refcount the socket.
 */
export function useWebsocketState() {
  return {
    isConnected: readonly(isConnected),
  }
}

export function useWebsocket() {
  onMounted(async () => {
    componentCount.value++
    await initializeConnection()
  })

  onUnmounted(() => {
    componentCount.value--
    nextTick(() => {
      disconnectIfUnused()
    })
  })

  return {
    socket: readonly(socket),
    isConnected: readonly(isConnected),
    isConnecting: readonly(isConnecting),
    addListener,
    removeListener,
    watchVm,
    unwatchVm,
    /**
     * Connect now. For the pages that START without a session and acquire one
     * in place — the checkout, where somebody signs up mid-purchase — because
     * the mount-time attempt correctly declined to connect back then.
     */
    connect: initializeConnection,
  }
}
